import React, { useState, useEffect, useReducer } from 'react';
import { Core } from 'cytoscape';

import { ThemeTag } from '@/features/analyze/components/Tag';
import { Node, NodeType } from '@/features/connect-dots/types/cytoscape';
import { useTranslation } from 'react-i18next';
import {
  HashtagIcon,
  TagIcon,
  ChevronUpIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { rwColors } from '@/styles/colors';
import { Toggle } from '@/components/Elements';
import { FilterItemMode } from '@/features/connect-dots/types/SelectedFilterItemsContextType';
import { useSelectedFilterItems } from '@/features/connect-dots/hooks/useSelectedFilterItems';
import { useThemes } from '@/features/theme/api/getThemes';

export interface TooltipPosition {
  x: number;
  y: number;
}

export interface NodeTooltipProps {
  content: Node | null;
  position: TooltipPosition;
  visible: boolean;
  onClose?: () => void; // Optional close handler
  cytoscapeCoreRefObject?: React.MutableRefObject<Core | null>;
}

// Reducer関数
const showNormalNodesReducer = (
  state: Record<string, boolean>,
  action: { type: 'TOGGLE'; value: string }
) => {
  switch (action.type) {
    case 'TOGGLE':
      return {
        ...state,
        [action.value]: !state[action.value], // 指定されたテーマIDの状態をトグル
      };
    default:
      return state;
  }
};

export const NodeTooltip: React.FC<NodeTooltipProps> = ({
  content,
  position,
  visible,
  onClose = () => {}, // デフォルトのonCloseハンドラ
  cytoscapeCoreRefObject,
}: NodeTooltipProps) => {
  const { data: contractingThemes = [], isLoading: isThemesLoading } =
    useThemes();
  const { t } = useTranslation();
  const { addNode } = useSelectedFilterItems();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownUp, setDropdownUp] = useState(false);

  // Reducerを使った状態管理
  const [showNormalNodes, dispatch] = useReducer(showNormalNodesReducer, {});

  // トグルスイッチの切り替えハンドラ
  const handleToggleNormalNodes = (value: string) => {
    dispatch({ type: 'TOGGLE', value });

    // ここで実際の表示/非表示のロジックを実装
    if (!cytoscapeCoreRefObject || !cytoscapeCoreRefObject.current) return;

    // ノードの表示・非表示を切り替え
    const nodes = cytoscapeCoreRefObject.current
      .nodes(`[nodeType = "${NodeType.NORMAL}"]`)
      .filter((node) => node.data('themes').includes(value));

    if (showNormalNodes[value]) {
      nodes.style('display', 'none'); // 非表示
    } else {
      nodes.style('display', 'element'); // 表示
    }
    // フィルタリング結果を適用
  };

  // ツールチップのサイズ
  const TOOLTIP_WIDTH = 420;
  const TOOLTIP_HEIGHT = 500; // おおよその高さ。必要に応じて調整

  // tooltipの位置を、ドラッグで移動できるようにする
  const [dragging, setDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState<TooltipPosition>({
    x: position.x,
    y: position.y,
  });

  /* tooltip のドラッグ操作対応 */
  // マウスダウン
  const handleMouseDown = () => {
    setDragging(true);
  };

  // イベントリスナーの登録と解除
  useEffect(() => {
    // マウスムーブ
    const handleMouseMove = (e: MouseEvent) => {
      if (dragging) {
        setDragPosition((prev) => ({
          x: prev.x + e.movementX,
          y: prev.y + e.movementY,
        }));
      }
    };

    // マウスアップ
    const handleMouseUp = () => {
      setDragging(false);
    };

    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging]);
  /* ドラッグ操作対応ここまで */

  useEffect(() => {
    if (!visible) {
      setDropdownOpen(false); // ツールチップが閉じられたときにドロップダウンを閉じる
      setDropdownUp(false); // ドロップダウンの向きもリセット
    }
  }, [visible, setDropdownUp, setDropdownOpen]);

  // ドロップダウンの向きを判定
  useEffect(() => {
    if (!dropdownOpen) return;
    const dropdownHeight = 150;
    if (position.y + TOOLTIP_HEIGHT + dropdownHeight > window.innerHeight) {
      setDropdownUp(true);
    } else {
      setDropdownUp(false);
    }
  }, [dropdownOpen, position.y, setDropdownUp]);

  // 画面内に収めるための位置補正
  let left = position.x;
  let top = position.y + 10;

  // 右端はみ出し補正
  if (left + TOOLTIP_WIDTH > window.innerWidth * 0.6) {
    left = left - TOOLTIP_WIDTH + 10;
  }
  // 下端はみ出し補正
  if (top + TOOLTIP_HEIGHT > window.innerHeight) {
    top = window.innerHeight - TOOLTIP_HEIGHT - 10;
  }
  // 上端はみ出し補正（必要なら）
  if (top < 0) top = 10;
  // 左端はみ出し補正（必要なら）
  if (left < 0) left = 10;

  useEffect(() => {
    setDragPosition({ x: left, y: top });
  }, [left, top]);

  if (!visible || !content) return null;

  const isThemeNameMatch = (() => {
    // テーマノードの場合、そのテーマが契約済みかチェック
    if (content.nodeType === NodeType.THEME) {
      const themeTableName = content.value;
      return (
        contractingThemes.some((theme) => theme.tableName === themeTableName) ??
        false
      );
    }

    // 関連テーマの中に一つでも契約済みのものがあれば選択可能
    return content.themes.some((themeTableName) =>
      contractingThemes.some((theme) => theme.tableName === themeTableName)
    );
  })();

  // ドロップダウンのオプション配列
  const filterOptions_topicTag = [
    {
      label: t('フィルター(NOT)に追加'),
      optionValue: 'add_to_not',
      modeValue: FilterItemMode.NOT,
    },
    {
      label: t('フィルター(OR)に追加'),
      optionValue: 'add_to_or',
      modeValue: FilterItemMode.OR,
    },
  ];

  const filterOptions_theme = [
    {
      label: t('フィルター(OR)に追加'),
      optionValue: 'add_to_or',
      modeValue: FilterItemMode.OR,
    },
  ];

  // ボタンクリック、オプション選択時の処理
  const handleOptionSelect = (
    modeValue: (typeof FilterItemMode)[keyof typeof FilterItemMode]
  ) => {
    const recordNode = {
      label: content.label,
      nodeType: content.nodeType,
      mode: modeValue,
      name: content.value,
      hasSelected: false, // 事前に選択されているわけではない
    };

    addNode(recordNode); // ノードを追加

    setDropdownOpen(false); // ドロップダウンを閉じる
    setDropdownUp(false); // ドロップダウンの向きをリセット
    if (onClose) onClose(); // ツールチップを閉じる
  };

  // ドロップダウンの開閉
  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const actionDisabled = !isThemeNameMatch;
  // 背景色（無効時 / 有効時）
  const disabledBg = '#9ca3af'; // 無効時の色（任意で調整）
  const enabledBg = rwColors.main;
  const hoverBg = rwColors.sub;

  return (
    <div
      className="pointer-events-auto absolute z-50 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-lg"
      style={{
        left: dragPosition.x,
        top: dragPosition.y,
        maxWidth: `${TOOLTIP_WIDTH}px`,
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.2s',
      }}
    >
      <div className="flex flex-col gap-2">
        {/* ドラッグ可能領域（ヘッダー部分） */}
        <div
          className="mb-1 flex cursor-move items-center justify-between gap-2"
          onMouseDown={handleMouseDown}
        >
          {/* themeタグの表示 */}
          {content.nodeType === NodeType.THEME ? (
            <span className="text-xs font-semibold text-blue-500">
              <HashtagIcon className="mr-1 inline-block size-4" />
              {t('テーマ')}
            </span>
          ) : (
            <div>
              {/* ISSUE TAG */}
              {content.nodeType === NodeType.CROSS_ISSUE ||
              content.nodeType === NodeType.SOCIAL_ISSUE ? (
                <span className="text-xs font-semibold text-red-500">
                  <TagIcon className="mr-1 inline-block size-4" />
                  {t('Issue Tags')}
                </span>
              ) : (
                <span className="text-xs font-semibold text-gray-500">
                  <TagIcon className="mr-1 inline-block size-4" />
                  {t('Normal Tags')}
                </span>
              )}
            </div>
          )}

          {/* クローズボタン */}
          <button
            className="justify-end text-gray-400 hover:text-gray-600"
            onClick={onClose}
            aria-label={t('閉じる')}
          >
            <XMarkIcon className="size-4" />
          </button>
        </div>

        {/* タイトル */}
        <div className="mb-1 text-lg font-bold leading-tight">
          {content.label}
        </div>

        {/* サブ説明（例: description） */}
        {content.nodeType !== NodeType.THEME &&
          (content.description ? (
            <div className="mb-2 text-xs text-gray-600">
              {content.description}
            </div>
          ) : (
            <div className="mb-2 text-xs text-gray-600">{t('説明未設定')}</div>
          ))}

        {/* 記事数 （非表示）*/}
        {/* <span className="ml-2 text-xs text-gray-400">
          {content.weight.toLocaleString('ja-JP')} {t('記事')}
        </span> */}

        {/* トグルスイッチの追加 */}
        {content.nodeType === NodeType.THEME && content.value && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-700">
              {t('ノーマルタグを表示')}
            </label>
            <div style={{ fontSize: '0.6rem' }}>
              <Toggle
                checked={showNormalNodes[content.value]}
                onChange={() => {
                  handleToggleNormalNodes(content.value);
                }}
                size="sm"
                label={showNormalNodes[content.value] ? t('表示') : t('非表示')}
                aria-label={`Toggle visibility of ${NodeType.NORMAL} nodes for theme ${content.value}`}
              />
            </div>
          </div>
        )}

        {/* 関連テーマ */}
        {content.nodeType !== NodeType.THEME && content.themes.length > 0 && (
          <>
            {t('関連テーマ')}:
            <div className="flex flex-wrap gap-1">
              {content.themes.map((themeTableName) => (
                <ThemeTag
                  key={themeTableName}
                  icon={HashtagIcon}
                  tableName={themeTableName}
                />
              ))}
            </div>
          </>
        )}

        {/* フィルター選択中表示 */}
        {content.isSelected && (
          <div className="mt-2 text-xs font-bold text-blue-500">
            {t('フィルターで選択中')}
          </div>
        )}

        {/* スプリットボタン（フィルターに追加＋ドロップダウン） */}
        {!content.isSelected && (
          <div
            className="pointer-events-auto relative mt-3 flex"
            style={{ backgroundColor: rwColors.main }}
          >
            {/* 主アクションボタン */}
            <button
              className="pointer-events-auto flex-1 rounded-l  px-4 py-1 text-sm font-semibold text-white"
              onClick={() => handleOptionSelect(FilterItemMode.AND)}
              disabled={actionDisabled}
              title={
                actionDisabled
                  ? t('このテーマの記事を閲覧するには有料契約が必要です。')
                  : undefined
              }
              style={
                {
                  cursor: actionDisabled ? 'not-allowed' : 'pointer',
                  backgroundColor: actionDisabled ? disabledBg : enabledBg,
                  opacity: actionDisabled ? 0.5 : 1,
                } as React.CSSProperties
              }
              onMouseEnter={(e) => {
                if (!actionDisabled) {
                  e.currentTarget.style.backgroundColor = hoverBg;
                }
              }}
              onMouseLeave={(e) => {
                if (!actionDisabled) {
                  e.currentTarget.style.backgroundColor = enabledBg;
                }
              }}
            >
              {isThemesLoading ? t('loading...') : t('フィルター(AND)に追加')}
            </button>
            {/* ドロップダウンボタン */}
            <div
              className="pointer-events-auto relative"
              style={{ cursor: 'pointer', backgroundColor: rwColors.main }}
            >
              <button
                className="pointer-events-auto flex items-center rounded-r border-l border-white p-1"
                style={
                  {
                    cursor: actionDisabled ? 'not-allowed' : 'pointer',
                    backgroundColor: actionDisabled ? disabledBg : enabledBg,
                    '--hover-bg-color': rwColors.sub,
                    opacity: actionDisabled ? 0.5 : 1,
                  } as React.CSSProperties
                }
                disabled={actionDisabled}
                onMouseEnter={(e) => {
                  if (!actionDisabled) {
                    e.currentTarget.style.backgroundColor = hoverBg;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!actionDisabled) {
                    e.currentTarget.style.backgroundColor = enabledBg;
                  }
                }}
                onClick={() => {
                  if (!actionDisabled) toggleDropdown();
                }}
              >
                {isThemesLoading ? (
                  ''
                ) : (
                  <ChevronUpIcon className="h-6 w-4 font-bold text-white" />
                )}
              </button>
              {/* ドロップダウンメニュー */}
              {dropdownOpen && (
                <div
                  className={`pointer-events-auto absolute z-10 w-48 overflow-hidden rounded border shadow-lg
                  ${dropdownUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}
                  style={{
                    backgroundColor: rwColors.main,
                    ...(dropdownUp ? { right: 0 } : { right: 0 }),
                  }}
                >
                  {content.nodeType == NodeType.THEME
                    ? filterOptions_theme.map((opt) => (
                        <button
                          key={opt.optionValue}
                          className="w-full p-4 text-left text-white hover:bg-blue-800"
                          onClick={() => handleOptionSelect(opt.modeValue)}
                          disabled={actionDisabled}
                          title={
                            actionDisabled
                              ? t(
                                  'このテーマの記事を閲覧するには有料契約が必要です。'
                                )
                              : undefined
                          }
                          style={
                            {
                              cursor: actionDisabled
                                ? 'not-allowed'
                                : 'pointer',
                              backgroundColor: actionDisabled
                                ? disabledBg
                                : enabledBg,
                              opacity: actionDisabled ? 0.5 : 1,
                            } as React.CSSProperties
                          }
                          onMouseEnter={(e) => {
                            if (!actionDisabled) {
                              e.currentTarget.style.backgroundColor = hoverBg;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!actionDisabled) {
                              e.currentTarget.style.backgroundColor = enabledBg;
                            }
                          }}
                        >
                          {opt.label}
                        </button>
                      ))
                    : filterOptions_topicTag.map((opt) => (
                        <button
                          key={opt.optionValue}
                          className="w-full p-4 text-left text-white hover:bg-blue-800"
                          onClick={() => handleOptionSelect(opt.modeValue)}
                          disabled={actionDisabled}
                          title={
                            actionDisabled
                              ? t(
                                  'このテーマの記事を閲覧するには有料契約が必要です。'
                                )
                              : undefined
                          }
                          style={
                            {
                              cursor: actionDisabled
                                ? 'not-allowed'
                                : 'pointer',
                              backgroundColor: actionDisabled
                                ? disabledBg
                                : enabledBg,
                              opacity: actionDisabled ? 0.5 : 1,
                            } as React.CSSProperties
                          }
                          onMouseEnter={(e) => {
                            if (!actionDisabled) {
                              e.currentTarget.style.backgroundColor = hoverBg;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!actionDisabled) {
                              e.currentTarget.style.backgroundColor = enabledBg;
                            }
                          }}
                        >
                          {opt.label}
                        </button>
                      ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
